import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import {
  Box,
  Flex,
  Text,
  Input,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@chakra-ui/react'
import Axios from '../../../Axios'
import {
  Button,
  TableBox,
  Loading,
  currentMonth,
  money,
  get,
  writers,
  useBillingAction
} from './shared'

const emptyRate = (from) => ({
  contract: 'UNIFORMADO',
  hierarchicalGroup: '',
  cprBand: '',
  amount: '',
  from,
  enabled: true,
  note: ''
})
export default function Rates() {
  const { user } = useSelector((s) => s.auth)
  const canWrite = writers.includes(user?.role)
  const { busy, action } = useBillingAction()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [month, setMonth] = useState(currentMonth)
  const [contract, setContract] = useState('')
  const [rate, setRate] = useState(() => emptyRate(currentMonth()))
  const [showHistory, setShowHistory] = useState(false)
  const rates = useQuery(['billing', 'rates'], () => get('rates'), {
    retry: false
  })
  const catalog = useQuery(['billing', 'catalog'], () => get('rates/catalog'), {
    staleTime: 300000
  })
  const all = rates.data || []
  const sorted = [...all].sort(
    (a, b) => b.from.localeCompare(a.from) || b._id.localeCompare(a._id)
  )
  const current = new Map()
  sorted.forEach((r) => {
    if (r.from <= month && !current.has(r.ruleKey)) current.set(r.ruleKey, r)
  })
  const rows = (showHistory ? sorted : [...current.values()])
    .filter(
      (r) =>
        (!contract || r.contract === contract) &&
        (showHistory || r.contract !== 'CPR' || r.cprBand)
    )
    .sort(
      (a, b) =>
        a.contract.localeCompare(b.contract) ||
        (a.contract === 'CPR'
          ? ['0', '1-12', '13+'].indexOf(a.cprBand) -
            ['0', '1-12', '13+'].indexOf(b.cprBand)
          : 0) ||
        (a.hierarchicalGroup || '').localeCompare(b.hierarchicalGroup || '') ||
        b.from.localeCompare(a.from)
    )
  const edit = (existing) => {
    setRate(
      existing ? { ...existing, from: month, note: '' } : emptyRate(month)
    )
    onOpen()
  }
  const save = (e) => {
    e.preventDefault()
    action(async () => {
      await Axios.post('/billing/rates', {
        ...rate,
        hierarchicalGroup:
          rate.contract === 'UNIFORMADO' ? rate.hierarchicalGroup : null,
        cprBand: rate.contract === 'CPR' ? rate.cprBand : null,
        amount: Number(rate.amount)
      })
      onClose()
    }, 'Regla de descuento guardada')
  }
  return (
    <Box minW={0}>
      <Flex align="center" justify="space-between" gap={4} wrap="wrap" mb={5}>
        <Text color="gray.600" maxW="700px">
          Uniformados utilizan la tabla de grupos jerárquicos. CPR utiliza su
          grado numérico: 0 $3.000; 1 a 12 $2.300; 13 en adelante $1.200.
        </Text>
        {canWrite && <Button onClick={() => edit()}>Agregar regla</Button>}
      </Flex>
      <Flex
        gap={4}
        align="end"
        wrap="wrap"
        pb={4}
        borderBottom="1px solid"
        borderColor="green.500"
      >
        <FormControl maxW="200px">
          <FormLabel htmlFor="rate-period">Consultar vigencia</FormLabel>
          <Input
            id="rate-period"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </FormControl>
        <FormControl maxW="240px">
          <FormLabel htmlFor="rate-contract-filter">Contrato</FormLabel>
          <Select
            id="rate-contract-filter"
            value={contract}
            onChange={(e) => setContract(e.target.value)}
          >
            <option value="">Todos los contratos</option>
            {catalog.data?.contracts.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </FormControl>
        <Checkbox
          pb={2}
          colorScheme="green"
          isChecked={showHistory}
          onChange={(e) => setShowHistory(e.target.checked)}
        >
          Ver todas las vigencias
        </Checkbox>
      </Flex>
      <Loading query={rates} />
      <Loading query={catalog} />
      <TableBox>
        <Table
          size="sm"
          sx={{
            '& th': { whiteSpace: 'normal' },
            '& td': { whiteSpace: 'normal', overflowWrap: 'anywhere' }
          }}
        >
          <Thead>
            <Tr>
              <Th>Contrato / grupo o tramo</Th>
              <Th isNumeric>Descuento mensual</Th>
              <Th>Desde</Th>
              <Th>Estado</Th>
              {showHistory && <Th>Motivo</Th>}
              {canWrite && <Th>Opciones</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {!rates.isLoading && !rows.length && (
              <Tr>
                <Td colSpan={6} py={6}>
                  Sin reglas para esta selección. Los contratos sin tarifa
                  quedan pendientes de definición.
                </Td>
              </Tr>
            )}
            {rows.map((r) => (
              <Tr key={r._id}>
                <Td>
                  <Text fontWeight="semibold">
                    {r.contract} ·{' '}
                    {r.contract === 'CPR'
                      ? catalog.data?.cprBands?.find((b) => b.key === r.cprBand)
                          ?.label || 'Tarifa única anterior (retirada)'
                      : r.hierarchicalGroup || 'Todos los grados'}
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    {catalog.data?.groups
                      .find((g) => g.name === r.hierarchicalGroup)
                      ?.grades.join(', ') ||
                      (r.contract === 'CPR'
                        ? 'Tramo según grado CPR, separado de la jerarquía'
                        : 'El grado no cambia esta tarifa')}
                  </Text>
                </Td>
                <Td isNumeric>{r.enabled ? money(r.amount) : '—'}</Td>
                <Td>{r.from}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      !r.enabled ? 'gray' : r.from > month ? 'blue' : 'green'
                    }
                  >
                    {!r.enabled
                      ? 'Deshabilitada'
                      : r.from > month
                      ? 'Programada'
                      : 'Habilitada'}
                  </Badge>
                </Td>
                {showHistory && <Td>{r.note}</Td>}
                {canWrite && (
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      bg="transparent"
                      color="green.500"
                      isDisabled={r.contract === 'CPR' && !r.cprBand}
                      onClick={() => edit(r)}
                    >
                      Cambiar
                    </Button>
                  </Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableBox>
      <Text mt={4} fontSize="sm" color="gray.600">
        Las vigencias conservan las versiones anteriores. Cambiar una regla no
        modifica una nómina guardada ni un esperado reconstruido. Para revisar
        un mes histórico, utiliza «Reconstruir esperado» en Carga de cobro.
      </Text>
      <Modal
        isOpen={isOpen}
        onClose={busy ? () => {} : onClose}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent as="form" onSubmit={save} borderRadius={20} mx={4}>
          <ModalHeader color="green.500">Regla de descuento</ModalHeader>
          <ModalCloseButton isDisabled={busy} />
          <ModalBody>
            <FormControl isRequired mb={4}>
              <FormLabel htmlFor="rate-contract">Contrato</FormLabel>
              <Select
                id="rate-contract"
                value={rate.contract}
                onChange={(e) =>
                  setRate({
                    ...rate,
                    contract: e.target.value,
                    hierarchicalGroup: '',
                    cprBand: ''
                  })
                }
              >
                {catalog.data?.contracts.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </Select>
            </FormControl>
            {rate.contract === 'UNIFORMADO' ? (
              <FormControl isRequired mb={4}>
                <FormLabel htmlFor="rate-group">Grupo jerárquico</FormLabel>
                <Select
                  id="rate-group"
                  value={rate.hierarchicalGroup}
                  onChange={(e) =>
                    setRate({ ...rate, hierarchicalGroup: e.target.value })
                  }
                >
                  <option value="">Seleccione un grupo</option>
                  {catalog.data?.groups.map((g) => (
                    <option key={g.name}>{g.name}</option>
                  ))}
                </Select>
              </FormControl>
            ) : rate.contract === 'CPR' ? (
              <FormControl isRequired mb={4}>
                <FormLabel htmlFor="rate-cpr-band">
                  Tramo de grado CPR
                </FormLabel>
                <Select
                  id="rate-cpr-band"
                  value={rate.cprBand || ''}
                  onChange={(e) =>
                    setRate({ ...rate, cprBand: e.target.value })
                  }
                >
                  <option value="">Seleccione un tramo</option>
                  {catalog.data?.cprBands?.map((b) => (
                    <option key={b.key} value={b.key}>
                      {b.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Text mb={4}>
                Tarifa única para todos los grados de este contrato.
              </Text>
            )}
            <FormControl isRequired={rate.enabled} mb={4}>
              <FormLabel htmlFor="rate-amount">Monto mensual ($)</FormLabel>
              <Input
                id="rate-amount"
                type="number"
                min="1"
                max="9999999"
                step="1"
                isDisabled={!rate.enabled}
                value={rate.amount}
                onChange={(e) => setRate({ ...rate, amount: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired mb={4}>
              <FormLabel htmlFor="rate-from">Vigente desde</FormLabel>
              <Input
                id="rate-from"
                type="month"
                value={rate.from}
                onChange={(e) => setRate({ ...rate, from: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired mb={4}>
              <FormLabel htmlFor="rate-note">Motivo del cambio</FormLabel>
              <Input
                id="rate-note"
                minLength={10}
                maxLength={500}
                value={rate.note}
                onChange={(e) => setRate({ ...rate, note: e.target.value })}
              />
            </FormControl>
            <Checkbox
              colorScheme="green"
              isChecked={rate.enabled}
              onChange={(e) => setRate({ ...rate, enabled: e.target.checked })}
            >
              Regla habilitada
            </Checkbox>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              bg="beige.500"
              color="green.500"
              onClick={onClose}
              isDisabled={busy}
            >
              Cancelar
            </Button>
            <Button type="submit" isLoading={busy}>
              Guardar descuento
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
